// ---------------------------------------------------------------------------
// AG-028 Phase 2 - Agency Marketplace / Franchise Package Schema
// ---------------------------------------------------------------------------
// Story AG-028: "Agency marketplace/franchise packages allow agencies to
// package workflows/templates/reports and install/customize for clients."
//
// Phase 1 (LANDED) shipped the pure classifier in
// `packages/platform/src/services/marketplace/ag-028-marketplace-package-classifier.ts`
// with closed package-kind / installability / customisation-policy /
// blocker vocabularies.
//
// Phase 5 (LANDED) mirrored those vocabularies into
// `packages/db/src/schema/ag-028-agency-marketplace-event-catalog.ts` so
// future emitters and DLQ consumers can depend on a locked DB-tier contract.
//
// Phase 2 (this file) is the persistence tranche. It stores:
// - the package composition the submit route classified
// - the classifier result (`packageKind`, `status`, `blockedRules`, reasons)
// - the seller + idempotency identity (`tenant_id`, `owner_agency_id`,
//   `listing_key`)
//
// Design rules locked here:
// - One row per `(tenant_id, listing_key)` where `listing_key` is a stable
//   hash over the submit surface's package identity. Re-submitting the same
//   version updates the classifier output instead of creating duplicates.
// - `status` reuses the locked AG-028 installability vocabulary verbatim so
//   Phase 3 can persist `classifyMarketplacePackage(...).installability`
//   directly with no string mapping.
// - `package_kind`, `customisation_policy`, and `blocked_rules` reuse the
//   locked Phase 5 vocabularies verbatim. The DB fails closed on vocabulary
//   drift even if a future caller bypasses the route validator.
// - Blocked submissions are intentionally persisted for audit and operator
//   follow-up. The schema does NOT repeat Phase 1 input validation like
//   semver or "at least one artifact" because those rows still need to be
//   stored with `status = blocked` and the fired rule codes.
// - `workflow_ids`, `template_ids`, `report_ids`, and `dependencies` are
//   `text[]` because the classifier contract only requires string arrays and
//   these ids span multiple domain surfaces. Phase 2 stays storage-only and
//   avoids guessing cross-table FK homogeneity that the registry has not
//   locked yet.
// ---------------------------------------------------------------------------

import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  index,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";

import {
  AG_028_AGENCY_MARKETPLACE_BLOCKER_VOCAB,
  AG_028_AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB,
  AG_028_AGENCY_MARKETPLACE_INSTALLABILITY_VOCAB,
  AG_028_AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB,
} from "./ag-028-agency-marketplace-event-catalog";

const tz = { withTimezone: true } as const;

export const AGENCY_MARKETPLACE_PACKAGE_STATUSES =
  AG_028_AGENCY_MARKETPLACE_INSTALLABILITY_VOCAB;

export type AgencyMarketplacePackageStatus =
  (typeof AGENCY_MARKETPLACE_PACKAGE_STATUSES)[number];

export function isAgencyMarketplacePackageStatus(
  value: unknown,
): value is AgencyMarketplacePackageStatus {
  return (
    typeof value === "string" &&
    (AGENCY_MARKETPLACE_PACKAGE_STATUSES as readonly string[]).includes(value)
  );
}

export const AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB =
  AG_028_AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB;

export type AgencyMarketplacePackageKindVocab =
  (typeof AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB)[number];

export const AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB =
  AG_028_AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB;

export type AgencyMarketplaceCustomisationPolicyVocab =
  (typeof AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB)[number];

export const AGENCY_MARKETPLACE_PACKAGE_BLOCKER_VOCAB =
  AG_028_AGENCY_MARKETPLACE_BLOCKER_VOCAB;

export type AgencyMarketplacePackageBlockerVocab =
  (typeof AGENCY_MARKETPLACE_PACKAGE_BLOCKER_VOCAB)[number];

export const agencyMarketplacePackages = pgTable(
  "agency_marketplace_packages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /** Seller tenant scope; later install routes filter by this tenant id. */
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Agency owner identity from the Phase 1 classifier input. Stored as
     * text because the classifier + dependency namespace treat it as an
     * opaque string token, not a locked UUID FK.
     */
    ownerAgencyId: text("owner_agency_id").notNull(),
    /**
     * Stable hash over the package identity so Phase 3 submit can be
     * idempotent on re-submit.
     */
    listingKey: text("listing_key").notNull(),
    packageName: text("package_name").notNull(),
    version: text("version").notNull(),
    packageKind: text("package_kind").notNull(),
    /** Direct mirror of Phase 1 `installability`. */
    status: text("status").notNull(),
    /**
     * The submitter-declared franchise intent. Stored separately from
     * `packageKind` because blocked submissions still need the original
     * author intent for audit/debugging.
     */
    isFranchise: boolean("is_franchise").notNull().default(false),
    /**
     * Direct mirror of Phase 1 `customisationPolicy`. Default stays aligned
     * with the classifier's omitted-input default (`branding_only`).
     */
    customisationPolicy: text("customisation_policy")
      .notNull()
      .default("branding_only"),
    workflowIds: text("workflow_ids").array().notNull().default(sql`'{}'`),
    templateIds: text("template_ids").array().notNull().default(sql`'{}'`),
    reportIds: text("report_ids").array().notNull().default(sql`'{}'`),
    dependencies: text("dependencies").array().notNull().default(sql`'{}'`),
    /** Phase 1 `blockedRules` persisted for operator follow-up and audit. */
    blockedRules: text("blocked_rules").array().notNull().default(sql`'{}'`),
    /** Phase 1 `reasons` strings persisted as a route/UI explainer surface. */
    classificationReasons: text("classification_reasons")
      .array()
      .notNull()
      .default(sql`'{}'`),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("agency_marketplace_packages_tenant_key_uq").on(
      table.tenantId,
      table.listingKey,
    ),
    index("agency_marketplace_packages_tenant_status_idx").on(
      table.tenantId,
      table.status,
    ),
    index("agency_marketplace_packages_owner_status_idx").on(
      table.ownerAgencyId,
      table.status,
    ),
    check(
      "agency_marketplace_packages_status_ck",
      sql`${table.status} IN (${sql.join(
        AGENCY_MARKETPLACE_PACKAGE_STATUSES.map((status) => sql`${status}`),
        sql`, `,
      )})`,
    ),
    check(
      "agency_marketplace_packages_kind_ck",
      sql`${table.packageKind} IN (${sql.join(
        AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB.map((kind) => sql`${kind}`),
        sql`, `,
      )})`,
    ),
    check(
      "agency_marketplace_packages_customisation_policy_ck",
      sql`${table.customisationPolicy} IN (${sql.join(
        AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB.map(
          (policy) => sql`${policy}`,
        ),
        sql`, `,
      )})`,
    ),
    check(
      "agency_marketplace_packages_blocked_rules_ck",
      sql`${table.blockedRules} <@ ARRAY[${sql.join(
        AGENCY_MARKETPLACE_PACKAGE_BLOCKER_VOCAB.map((rule) => sql`${rule}`),
        sql`, `,
      )}]::text[]`,
    ),
    check(
      "agency_marketplace_packages_status_semantics_ck",
      sql`(
        (${table.status} = 'installable' AND ${table.packageKind} <> 'mixed_franchise' AND cardinality(${table.blockedRules}) = 0)
        OR (${table.status} = 'requires_review' AND ${table.packageKind} = 'mixed_franchise' AND cardinality(${table.blockedRules}) = 0)
        OR (${table.status} = 'blocked' AND cardinality(${table.blockedRules}) > 0)
      )`,
    ),
  ],
);

export type AgencyMarketplacePackageRow =
  typeof agencyMarketplacePackages.$inferSelect;
export type NewAgencyMarketplacePackageRow =
  typeof agencyMarketplacePackages.$inferInsert;
